// src/utils/sessionService.js
// Session tracking service for security management

import { db } from "../firebase";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  orderBy,
  limit,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";

/**
 * Generate a unique session ID
 */
export function generateSessionId() {
  return uuidv4();
}

/**
 * Get device information from user agent
 */
export function getDeviceInfo() {
  const ua = navigator.userAgent;
  let device = "Unknown Device";
  let browser = "Unknown Browser";
  let os = "Unknown OS";

  // Detect OS
  if (/Windows/.test(ua)) os = "Windows";
  else if (/Mac/.test(ua)) os = "macOS";
  else if (/Linux/.test(ua)) os = "Linux";
  else if (/Android/.test(ua)) os = "Android";
  else if (/iPhone|iPad/.test(ua)) os = "iOS";

  // Detect Browser
  if (/Chrome/.test(ua) && !/Edge/.test(ua)) browser = "Chrome";
  else if (/Safari/.test(ua) && !/Chrome/.test(ua)) browser = "Safari";
  else if (/Firefox/.test(ua)) browser = "Firefox";
  else if (/Edge/.test(ua)) browser = "Edge";

  // Detect Device Type
  if (/Mobile/.test(ua)) device = "Mobile";
  else if (/Tablet/.test(ua)) device = "Tablet";
  else device = "Desktop";

  return {
    device: `${device} - ${os}`,
    browser,
    userAgent: ua,
  };
}

/**
 * Get approximate location from IP (using free API)
 */
export async function getLocationInfo() {
  try {
    const response = await fetch("https://ipapi.co/json/", { timeout: 5000 });
    if (response.ok) {
      const data = await response.json();
      return {
        city: data.city || "Unknown",
        region: data.region || "",
        country: data.country_name || "Unknown",
        ip: data.ip || "Unknown",
        location: `${data.city || "Unknown"}, ${data.country_name || "Unknown"}`,
      };
    }
  } catch (error) {
    console.warn("Could not fetch location info:", error);
  }

  return {
    city: "Unknown",
    region: "",
    country: "Unknown",
    ip: "Unknown",
    location: "Unknown Location",
  };
}

/**
 * Create a new session on login
 */
export async function createSession(businessId, userId, userEmail) {
  if (!businessId || !userId) {
    console.error("Missing businessId or userId for session creation");
    return null;
  }

  const sessionId = generateSessionId();
  const deviceInfo = getDeviceInfo();
  const locationInfo = await getLocationInfo();

  const sessionData = {
    userId,
    userEmail,
    sessionId,
    device: deviceInfo.device,
    browser: deviceInfo.browser,
    userAgent: deviceInfo.userAgent,
    ip: locationInfo.ip,
    location: locationInfo.location,
    city: locationInfo.city,
    country: locationInfo.country,
    active: true,
    createdAt: serverTimestamp(),
    lastActive: serverTimestamp(),
  };

  try {
    // Check max concurrent sessions
    const securityRef = doc(db, "businesses", businessId, "settings", "security");
    const securitySnap = await getDoc(securityRef);
    const maxSessions = securitySnap.exists() ? securitySnap.data().maxConcurrentSessions || 5 : 5;

    if (maxSessions > 0) {
      // Get active sessions for this user
      const sessionsRef = collection(db, "businesses", businessId, "sessions");
      const activeQuery = query(
        sessionsRef,
        where("userId", "==", userId),
        where("active", "==", true),
        orderBy("createdAt", "asc")
      );
      const activeSnap = await getDocs(activeQuery);

      // If at max, terminate oldest session
      if (activeSnap.size >= maxSessions) {
        const oldestSession = activeSnap.docs[0];
        await updateDoc(doc(db, "businesses", businessId, "sessions", oldestSession.id), {
          active: false,
          terminatedAt: serverTimestamp(),
          terminatedReason: "max_sessions_exceeded",
        });
      }
    }

    // Create new session
    await setDoc(doc(db, "businesses", businessId, "sessions", sessionId), sessionData);

    // Store session ID in localStorage for current session identification
    localStorage.setItem("effitrack_session_id", sessionId);

    return sessionId;
  } catch (error) {
    console.error("Error creating session:", error);
    return null;
  }
}

/**
 * Update session last active timestamp
 */
export async function updateSessionActivity(businessId, sessionId) {
  if (!businessId || !sessionId) return;

  try {
    const sessionRef = doc(db, "businesses", businessId, "sessions", sessionId);
    await updateDoc(sessionRef, {
      lastActive: serverTimestamp(),
    });
  } catch (error) {
    console.warn("Error updating session activity:", error);
  }
}

/**
 * Terminate a session
 */
export async function terminateSession(businessId, sessionId, reason = "user_logout") {
  if (!businessId || !sessionId) return;

  try {
    const sessionRef = doc(db, "businesses", businessId, "sessions", sessionId);
    await updateDoc(sessionRef, {
      active: false,
      terminatedAt: serverTimestamp(),
      terminatedReason: reason,
    });

    // Clear local session if it's the current one
    const localSessionId = localStorage.getItem("effitrack_session_id");
    if (localSessionId === sessionId) {
      localStorage.removeItem("effitrack_session_id");
    }
  } catch (error) {
    console.error("Error terminating session:", error);
  }
}

/**
 * Terminate all sessions for a user (except current)
 */
export async function terminateAllUserSessions(businessId, userId, exceptSessionId = null) {
  if (!businessId || !userId) return;

  try {
    const sessionsRef = collection(db, "businesses", businessId, "sessions");
    const activeQuery = query(
      sessionsRef,
      where("userId", "==", userId),
      where("active", "==", true)
    );
    const snapshot = await getDocs(activeQuery);

    const terminatePromises = snapshot.docs
      .filter((doc) => doc.id !== exceptSessionId)
      .map((doc) =>
        updateDoc(doc.ref, {
          active: false,
          terminatedAt: serverTimestamp(),
          terminatedReason: "terminate_all",
        })
      );

    await Promise.all(terminatePromises);
  } catch (error) {
    console.error("Error terminating all sessions:", error);
  }
}

/**
 * Get active sessions for current user
 */
export async function getActiveSessions(businessId, userId) {
  if (!businessId || !userId) return [];

  try {
    const sessionsRef = collection(db, "businesses", businessId, "sessions");
    const activeQuery = query(
      sessionsRef,
      where("userId", "==", userId),
      where("active", "==", true),
      orderBy("lastActive", "desc")
    );
    const snapshot = await getDocs(activeQuery);

    return snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
  } catch (error) {
    console.error("Error fetching active sessions:", error);
    return [];
  }
}

/**
 * Check if session is still valid (not expired)
 */
export async function validateSession(businessId, sessionId) {
  if (!businessId || !sessionId) return false;

  try {
    const sessionRef = doc(db, "businesses", businessId, "sessions", sessionId);
    const sessionSnap = await getDoc(sessionRef);

    if (!sessionSnap.exists() || !sessionSnap.data().active) {
      return false;
    }

    // Check session timeout
    const securityRef = doc(db, "businesses", businessId, "settings", "security");
    const securitySnap = await getDoc(securityRef);
    const timeoutMinutes = securitySnap.exists() ? securitySnap.data().sessionTimeout || 30 : 30;

    const lastActive = sessionSnap.data().lastActive?.toDate();
    if (lastActive) {
      const now = new Date();
      const diffMinutes = (now - lastActive) / (1000 * 60);

      if (diffMinutes > timeoutMinutes) {
        // Session expired
        await terminateSession(businessId, sessionId, "timeout");
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error("Error validating session:", error);
    return false;
  }
}

/**
 * Get current session ID from localStorage
 */
export function getCurrentSessionId() {
  return localStorage.getItem("effitrack_session_id");
}

/**
 * Clean up old inactive sessions (call periodically or on login)
 */
export async function cleanupOldSessions(businessId, daysOld = 30) {
  if (!businessId) return;

  try {
    const sessionsRef = collection(db, "businesses", businessId, "sessions");
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysOld);

    const oldQuery = query(
      sessionsRef,
      where("active", "==", false),
      where("terminatedAt", "<", cutoffDate),
      limit(100)
    );

    const snapshot = await getDocs(oldQuery);
    const deletePromises = snapshot.docs.map((doc) => deleteDoc(doc.ref));
    await Promise.all(deletePromises);

    console.log(`Cleaned up ${snapshot.size} old sessions`);
  } catch (error) {
    console.warn("Error cleaning up old sessions:", error);
  }
}

export default {
  generateSessionId,
  getDeviceInfo,
  getLocationInfo,
  createSession,
  updateSessionActivity,
  terminateSession,
  terminateAllUserSessions,
  getActiveSessions,
  validateSession,
  getCurrentSessionId,
  cleanupOldSessions,
};
